import { useState } from "react";
import { TimestampsArr } from "../../../../utilities/useTimestamps";
import { loadVideoAtTime, waitForVideoLoad } from "./proctoringResultViewHelper";
import { binSearch } from "../../../../utilities/timestampBinSearch";

export type Thumbnail = { startTime?: number; endTime?: number; canvas?: HTMLCanvasElement };

type useThumbnailProps = {
	initialNumThumbnails: number;
	duration?: number;
	timeOffset: number;
};

export default function useThumbnails({ initialNumThumbnails, duration, timeOffset }: useThumbnailProps) {
	const [thumbnails, setThumbnails] = useState<Thumbnail[]>(Array(initialNumThumbnails).fill({}));

	const loadThumbnailsByKey = async (videoSources: Record<string, string>, timeStampArr: TimestampsArr) => {
		if (!videoSources || Object.keys(videoSources).length === 0) {
			console.warn("No video sources found for thumbnails.");
			return;
		}
		let currentSource: string = String(Object.keys(videoSources)[0]);

		const hiddenVideo = document.createElement("video");
		hiddenVideo.width = 96;
		hiddenVideo.height = 72;
		hiddenVideo.src = `${videoSources?.[currentSource]}`;

		await waitForVideoLoad(hiddenVideo);

		if (duration) {
			const thumbnailsCopy = [...thumbnails];
			const timeFrag = (duration - timeOffset) / initialNumThumbnails;
			for (const [index, _thumbnail] of thumbnailsCopy.entries()) {
				const time = (timeFrag * index + timeOffset) / 1000;
				const canvas = document.createElement("canvas");
				canvas.width = hiddenVideo.videoWidth;
				canvas.height = hiddenVideo.videoHeight;
				const context = canvas.getContext("2d");

				// get webcam video for given time
				const webcamTimestamp = timeStampArr[binSearch(timeStampArr, time)];
				if (currentSource !== String(webcamTimestamp.vid)) {
					// update hidden video source to current webcam video
					currentSource = String(webcamTimestamp.vid);
					hiddenVideo.src = `${videoSources?.[currentSource]}`;
				}
				await loadVideoAtTime(hiddenVideo, Math.max(0.1, time - (webcamTimestamp?.start ?? 0)))
					.then(() => {
						if (context && hiddenVideo.videoWidth > 0 && hiddenVideo.videoHeight > 0) {
							context.drawImage(hiddenVideo, 0, 0, hiddenVideo.videoWidth, hiddenVideo.videoHeight);
						}
					})
					.catch(() => {
						console.warn("Thumbnail video could not load.");
					});
				thumbnailsCopy[index] = {
					startTime: time,
					endTime: time + timeFrag / 1000,
					canvas: canvas,
				};
			}
			setThumbnails(thumbnailsCopy);
		}
	};

	const loadThumbnailsByIndex = async (videoSources: Record<string, string>, timeStampArr: TimestampsArr) => {
		let currentSource = 0;

		const hiddenVideo = document.createElement("video");
		hiddenVideo.width = 96;
		hiddenVideo.height = 72;
		hiddenVideo.src = `${videoSources?.[currentSource]}`;

		await waitForVideoLoad(hiddenVideo);

		if (duration) {
			const thumbnailsCopy = [...thumbnails];
			const timeFrag = duration / initialNumThumbnails;
			for (const [index, _thumbnail] of thumbnailsCopy.entries()) {
				const time = (timeFrag * index) / 1000;
				const canvas = document.createElement("canvas");
				canvas.width = hiddenVideo.videoWidth;
				canvas.height = hiddenVideo.videoHeight;
				const context = canvas.getContext("2d");

				// get webcam video for given time
				const webcamTimestamp = timeStampArr[binSearch(timeStampArr, time)];
				if (currentSource < webcamTimestamp.vid) {
					// update hidden video source to current webcam video
					currentSource = webcamTimestamp.vid;
					hiddenVideo.src = `${videoSources?.[currentSource]}`;
				}
				await loadVideoAtTime(hiddenVideo, Math.max(0.1, time - (webcamTimestamp?.start ?? 0)))
					.then(() => {
						if (context && hiddenVideo.videoWidth > 0 && hiddenVideo.videoHeight > 0) {
							context.drawImage(hiddenVideo, 0, 0, hiddenVideo.videoWidth, hiddenVideo.videoHeight);
						}
					})
					.catch(() => {
						console.warn("Thumbnail video could not load.");
					});
				thumbnailsCopy[index] = {
					startTime: time,
					endTime: time + timeFrag / 1000,
					canvas: canvas,
				};
			}
			setThumbnails(thumbnailsCopy);
		}
	};

	return {
		thumbnails,
		loadThumbnailsByKey,
		loadThumbnailsByIndex,
	};
}
